import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { NavLink, useNavigate } from 'react-router-dom';
import {
  LayoutGrid,
  RadioTower,
  BarChart3,
  ShieldCheck,
  Megaphone,
  Users,
  Building2,
  Smartphone,
  Tablet,
  MessagesSquare,
  Package,
  FileBarChart,
  Cable,
  LockKeyhole,
  UserCog,
  ScrollText,
  Settings as SettingsIcon,
  LogOut,
  Bell,
  ChevronDown,
  Menu as MenuIcon,
  X,
  Sun,
  Moon,
} from 'lucide-react';
import Dropdown, { DropdownItem } from '../components/ui/Dropdown';
import AuthService from '../services/AuthService';
import CommandMessageService from '../services/CommandMessageService';
import { isPathAllowed } from '../config/pageAccess';
import { useThemeMode } from '../context/ThemeContext';

const NAV_GROUPS = [
  {
    label: 'Overview',
    items: [
      { title: 'Admin Dashboard', path: '/dashboard', icon: LayoutGrid },
      { title: 'Command Center', path: '/command', icon: RadioTower },
      { title: 'High Level Dashboard', path: '/executive', icon: BarChart3 },
      { title: 'Team Leader', path: '/team-leader', icon: ShieldCheck },
    ],
  },
  {
    label: 'Operations',
    items: [
      { title: 'Announcements', path: '/activation', icon: Megaphone },
      { title: 'Teams Management', path: '/team', icon: Users },
      { title: 'Hospitals', path: '/hospitals', icon: Building2 },
      { title: 'Staff Mobile App', path: '/staff-app', icon: Smartphone },
      { title: 'Patient Documentation', path: '/patient-app', icon: Tablet },
      { title: 'Stakeholders', path: '/stakeholders', icon: MessagesSquare },
      { title: 'Inventory', path: '/inventory', icon: Package },
    ],
  },
  {
    label: 'System',
    items: [
      { title: 'Reports & KPI', path: '/reports', icon: FileBarChart },
      { title: 'User Management', path: '/users', icon: UserCog },
      { title: 'Audit Logs', path: '/audit', icon: ScrollText },
      { title: 'Connectivity', path: '/integrations', icon: Cable },
      { title: 'Governance', path: '/governance', icon: LockKeyhole },
      { title: 'Settings', path: '/settings', icon: SettingsIcon },
    ],
  },
];

function NavItem({ title, path, icon: Icon, onClick }) {
  return (
    <NavLink
      to={path}
      onClick={onClick}
      className={({ isActive }) =>
        `group flex items-center px-3 py-2.5 text-sm font-medium rounded-lg transition-all ${
          isActive
            ? 'bg-seha-500 text-white'
            : 'text-neutral-600 hover:bg-neutral-50 hover:text-seha-500 dark:text-neutral-300 dark:hover:bg-ink-800'
        }`
      }
    >
      {({ isActive }) => (
        <>
          <Icon
            className={`w-4 h-4 mr-3 ${
              isActive ? 'text-white' : 'text-neutral-400 group-hover:text-seha-500 dark:text-neutral-500'
            }`}
          />
          {title}
        </>
      )}
    </NavLink>
  );
}

NavItem.propTypes = {
  title: PropTypes.string.isRequired,
  path: PropTypes.string.isRequired,
  icon: PropTypes.elementType.isRequired,
  onClick: PropTypes.func,
};

function SidebarNav({ onNavigate }) {
  const tier = AuthService.getUser()?.accessTier;
  const visibleGroups = NAV_GROUPS
    .map((group) => ({ ...group, items: group.items.filter((item) => isPathAllowed(item.path, tier)) }))
    .filter((group) => group.items.length > 0);

  return (
    <>
      <div className="h-20 flex items-center py-2 px-6 border-b border-neutral-100 dark:border-neutral-800 shrink-0">
        <img src="/sehaLogo.jpeg" alt="SEHA Logo" className="py-0.5 h-16 mx-auto w-auto object-contain" />
      </div>
      <nav className="flex-1 overflow-y-auto py-6 px-4 space-y-1.5">
        {visibleGroups.map((group) => (
          <div key={group.label}>
            <div className="px-2 mb-2 mt-6 first:mt-0 text-[10px] font-medium text-neutral-400 dark:text-neutral-500 uppercase tracking-widest">
              {group.label}
            </div>
            <div className="space-y-1.5">
              {group.items.map((item) => (
                <NavItem key={item.path} {...item} onClick={onNavigate} />
              ))}
            </div>
          </div>
        ))}
      </nav>
      <div className="p-4 border-t border-neutral-100 dark:border-neutral-800 mt-auto shrink-0">
        <button
          type="button"
          className="flex items-center gap-3 w-full px-3 py-2.5 rounded-lg text-left text-red-600 hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-500/10 border border-transparent"
          onClick={() => {
            AuthService.logout();
            window.location.href = '/login';
          }}
        >
          <LogOut className="w-4 h-4" />
          <span className="text-sm font-medium">Logout</span>
        </button>
      </div>
    </>
  );
}

function Sidebar(props) {
  const { title, header, children } = props;
  const [mobileOpen, setMobileOpen] = useState(false);
  const navigate = useNavigate();
  const { mode, toggleTheme } = useThemeMode();

  const [unreadCount, setUnreadCount] = useState(0);
  const [messages, setMessages] = useState([]);
  const [messagesLoaded, setMessagesLoaded] = useState(false);

  useEffect(() => {
    const poll = async () => {
      try {
        const { data } = await CommandMessageService.unreadCount();
        setUnreadCount(data.count);
      } catch {
        // ignore — bell just stays at its last known count
      }
    };
    poll();
    const intervalId = setInterval(poll, 30000);
    return () => clearInterval(intervalId);
  }, []);

  const loadMessages = async () => {
    try {
      const { data } = await CommandMessageService.findAll(10);
      setMessages(data);
      setMessagesLoaded(true);
    } catch {
      // ignore
    }
  };

  return (
    <div className="bg-neutral-50 text-neutral-600 dark:bg-ink-950 dark:text-neutral-300 h-screen flex overflow-hidden antialiased">
      <aside className="hidden lg:flex w-72 bg-white dark:bg-ink-900 flex-col shrink-0 z-30 border-r border-neutral-100 dark:border-neutral-800">
        <SidebarNav />
      </aside>

      {mobileOpen && (
        <div className="fixed inset-0 z-40 lg:hidden">
          <div className="absolute inset-0 bg-neutral-900/40 dark:bg-black/60" onClick={() => setMobileOpen(false)} />
          <aside className="absolute left-0 top-0 bottom-0 w-72 bg-white dark:bg-ink-900 flex flex-col">
            <SidebarNav onNavigate={() => setMobileOpen(false)} />
          </aside>
        </div>
      )}

      <div className="flex-1 flex flex-col min-w-0">
        <header className="h-20 bg-white dark:bg-ink-900 border-b border-neutral-100 dark:border-neutral-800 flex items-center justify-between px-6 z-20 relative shrink-0">
          <div className="flex items-center gap-3 min-w-0">
            <button
              type="button"
              className="lg:hidden text-neutral-400 hover:text-neutral-700 dark:text-neutral-500 dark:hover:text-neutral-200"
              onClick={() => setMobileOpen((o) => !o)}
            >
              {mobileOpen ? <X className="w-5 h-5" /> : <MenuIcon className="w-5 h-5" />}
            </button>
            <div className="flex flex-col min-w-0">
              <h2 className="text-xl font-bold text-neutral-900 dark:text-neutral-50 leading-none truncate">{title}</h2>
              <span className="text-[11px] text-neutral-400 dark:text-neutral-500 mt-1">Live Command</span>
            </div>
          </div>

          <div className="flex items-center gap-4 shrink-0">
            {header}

            <div className="hidden xl:flex items-center gap-2 border-l border-neutral-100 dark:border-neutral-800 pl-4">
              <span className="inline-flex items-center gap-2 rounded-full border border-green-100 bg-green-50 px-3 py-2 text-xs font-semibold text-green-700 dark:border-green-500/20 dark:bg-green-500/10 dark:text-green-400">
                <span className="w-2 h-2 rounded-full bg-green-500 animate-pulse" /> Live Sync
              </span>
            </div>

            <button
              type="button"
              onClick={toggleTheme}
              className="p-2 text-neutral-400 hover:bg-neutral-50 hover:text-neutral-600 dark:text-neutral-500 dark:hover:bg-ink-800 dark:hover:text-neutral-300 rounded-lg"
              title={mode === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
            >
              {mode === 'dark' ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
            </button>

            <Dropdown
              trigger={({ toggle }) => (
                <button
                  onClick={() => { toggle(); if (!messagesLoaded) loadMessages(); }}
                  className="p-2 text-neutral-400 hover:bg-neutral-50 hover:text-neutral-600 dark:text-neutral-500 dark:hover:bg-ink-800 dark:hover:text-neutral-300 rounded-lg relative"
                >
                  <Bell className="w-5 h-5" />
                  {unreadCount > 0 && (
                    <span className="absolute top-2.5 right-2.5 w-2 h-2 bg-red-500 rounded-full border border-white dark:border-neutral-900" />
                  )}
                </button>
              )}
            >
              {() => (
                <div className="w-80">
                  <div className="px-4 py-3 border-b border-neutral-50 dark:border-neutral-800 flex items-center justify-between">
                    <p className="text-sm font-bold text-neutral-900 dark:text-neutral-50">Command Notifications</p>
                    <button
                      type="button"
                      className="text-xs text-seha-500 hover:underline"
                      onClick={async () => {
                        await CommandMessageService.markAllRead();
                        setUnreadCount(0);
                        loadMessages();
                      }}
                    >
                      Mark all read
                    </button>
                  </div>
                  <div className="max-h-96 overflow-y-auto">
                    {messages.length === 0 && (
                      <p className="px-4 py-6 text-xs text-neutral-400 dark:text-neutral-500 text-center">No notifications.</p>
                    )}
                    {messages.map((m) => (
                      <button
                        type="button"
                        key={m.id}
                        onClick={async () => {
                          if (!m.isRead) {
                            await CommandMessageService.markRead(m.id);
                            setUnreadCount((c) => Math.max(0, c - 1));
                          }
                          setMessages((prev) => prev.map((x) => (x.id === m.id ? { ...x, isRead: true } : x)));
                        }}
                        className="w-full text-left px-4 py-3 border-b border-neutral-50 hover:bg-neutral-50 dark:border-neutral-800 dark:hover:bg-ink-800 last:border-0"
                      >
                        <div className="flex items-center gap-2">
                          <span
                            className={`w-1.5 h-1.5 rounded-full shrink-0 ${
                              m.priority === 'Critical' ? 'bg-red-500' : m.priority === 'High' ? 'bg-orange-500' : 'bg-green-500'
                            } ${m.isRead ? 'opacity-30' : ''}`}
                          />
                          <p
                            className={`text-sm truncate ${
                              m.isRead ? 'text-neutral-500 dark:text-neutral-400' : 'font-semibold text-neutral-900 dark:text-neutral-50'
                            }`}
                          >
                            {m.title}
                          </p>
                        </div>
                        <p className="text-xs text-neutral-500 dark:text-neutral-400 mt-1 pl-3.5 line-clamp-2">{m.body}</p>
                        <p className="text-[10px] text-neutral-400 dark:text-neutral-500 mt-1 pl-3.5">{new Date(m.createdAt).toLocaleString()}</p>
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </Dropdown>

            <Dropdown
              trigger={({ toggle }) => (
                <button
                  onClick={toggle}
                  className="flex items-center gap-3 hover:bg-neutral-50 p-1.5 pr-2 rounded-lg transition-colors border border-transparent hover:border-neutral-100 dark:hover:bg-ink-800 dark:hover:border-neutral-700"
                >
                  <div className="w-10 h-10 rounded-full bg-seha-50 border border-seha-100 shadow-sm flex items-center justify-center text-seha-700 font-bold text-xs dark:bg-seha-500/10 dark:border-seha-500/20 dark:text-seha-300">
                    CC
                  </div>
                  <div className="hidden md:flex flex-col items-start">
                    <p className="font-semibold text-neutral-900 dark:text-neutral-50 leading-tight text-sm">{AuthService.getUser()?.fullname || 'CBRNE Command'}</p>
                    <p className="text-xs text-neutral-400 dark:text-neutral-500 font-medium capitalize">{AuthService.getUser()?.role || 'Admin'}</p>
                  </div>
                  <ChevronDown className="w-3.5 h-3.5 text-neutral-400 dark:text-neutral-500" />
                </button>
              )}
            >
              {({ close }) => (
                <>
                  <div className="px-4 py-3 border-b border-neutral-50 dark:border-neutral-800">
                    <p className="text-sm font-bold text-neutral-900 dark:text-neutral-50">{AuthService.getUser()?.fullname || 'SEHA CBRNE Command'}</p>
                    <p className="text-xs text-neutral-500 dark:text-neutral-400 truncate">{AuthService.getUser()?.email || ''}</p>
                  </div>
                  <DropdownItem
                    icon={LayoutGrid}
                    onClick={() => {
                      navigate('/dashboard');
                      close();
                    }}
                  >
                    Admin View
                  </DropdownItem>
                  <DropdownItem
                    icon={BarChart3}
                    onClick={() => {
                      navigate('/executive');
                      close();
                    }}
                  >
                    High Level View
                  </DropdownItem>
                  <DropdownItem
                    icon={ShieldCheck}
                    onClick={() => {
                      navigate('/team-leader');
                      close();
                    }}
                  >
                    Team Leader View
                  </DropdownItem>
                  <DropdownItem
                    icon={LogOut}
                    onClick={() => {
                      AuthService.logout();
                      window.location.href = '/login';
                    }}
                  >
                    Logout
                  </DropdownItem>
                </>
              )}
            </Dropdown>
          </div>
        </header>

        <main className="flex-1 overflow-auto bg-neutral-50 dark:bg-ink-950 relative p-6 lg:p-8">{children}</main>
      </div>
    </div>
  );
}

Sidebar.propTypes = {
  title: PropTypes.node,
  header: PropTypes.node,
};

export default Sidebar;
